import React, { FC, useState } from 'react';
import { ApprovalTableProps } from './ApprovalTableProps';
import { Stack } from '@mui/material';
import CaptureRevisedBudgetPrompt from '../CaptureRevisedBudgetPrompt';
import { getFY } from '../../utils/calculation';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import skapaColours from '@ingka/variables/colours-css';
import { groupColumn } from '../ProjectListTable/utils';
import { setupColumnsWidthAndOrder } from '../../hooks/useDatagridSettings';
import TableToolBar from '../TableToolBar';
import { viewClicked } from './utils';
import useTranslation from '../../hooks/useTranslation';
import { getIdFromLabel } from '../../utils/utils';
import Button from '@ingka/button';
import { FYBudgetFilter } from '../ProjectListTable/toolbar/FYBudgetFilter';
import { ProjectStatus } from '../../models/project';
import { ListBoxFooter } from '@ingka/list-box';

const fyOptions = [getFY(-1), getFY(), getFY(1)];

const ApprovalTable: FC<ApprovalTableProps> = ({
  projects,
  selectedCountries,
  onSelectedCountriesChange,
  countryOptions,
  tableSettings,
  tableSelection,
  onRowClick,
  approveProject,
  captureRevisedBudget,
  setTableSelection,
  archiveHandler,
  loading,
  columns,
}) => {
  const t = useTranslation();
  const subPortfolioOptions = t('ppm.subportfolios');
  const [selectedView, setSelectedView] = React.useState(['default']);
  const [filterAnchorEl, setFilterAnchorEl] =
    React.useState<HTMLElement | null>(null);
  const [isCaptureRevisedBudgetModal, setIsCaptureRevisedBudgetModal] =
    useState(false);
  const [selectedFY, setSelectedFY] = useState('');
  const gridApi = tableSettings.datagridProps.apiRef;

  const searchAppliedFilterValues = (key) => {
    return (
      tableSettings.datagridProps.filterModel.items
        ?.filter((item) => item.field === key)
        .map((item) => item.value)
        ?.flat(1) || []
    );
  };

  const toggleFilter = ({
    field,
    operator,
    value,
  }: {
    field: string;
    operator: string;
    value: string[];
  }) => {
    const id = `${field}-${operator}`;
    if (value.length === 0) {
      gridApi.current.deleteFilterItem({
        id,
        operator,
        field,
      });
      return;
    }
    gridApi.current.upsertFilterItem({
      field: field,
      operator,
      value,
      id: id,
    });
  };
  const toggleCountries = (all: boolean) => {
    const newCountries = all
      ? countryOptions.map((option) => option.value)
      : [];
    toggleFilter({
      field: 'countryCode',
      value: newCountries,
      operator: 'isAnyOf',
    });
    onSelectedCountriesChange(newCountries);
  };
  const isArchiveButtonVisible = tableSelection.every((projectId) => {
    const project = projects.find((el) => el.id === projectId);
    if (!project) return false;
    return (
      !project.realEstateSISAN &&
      (project?.projectStatus === ProjectStatus.CANCELLED ||
        project?.projectStatus === ProjectStatus.CLOSED)
    );
  });
  return (
    <Stack sx={{ inset: 0, flexGrow: 1 }}>
      <DataGridPremium
        apiRef={gridApi}
        {...tableSettings.datagridProps}
        initialState={{
          sorting: {
            sortModel: [{ field: 'lastUpdatedDate', sort: 'desc' }],
          },
        }}
        loading={loading}
        rowHeight={64}
        onRowClick={(params) => {
          tableSettings.datagridProps.onRowClick(params);
          if (params?.id?.toString().includes('auto-generated-row')) return;
          onRowClick(params?.id as string);
        }}
        sx={{
          border: 0,
          '.MuiDataGrid-row:has(.crp-cell--overflow-visible)': {
            overflow: 'visible',
            position: 'relative',
          },
          '.MuiDataGrid-row:has(.crp-cell--overflow-visible):hover': {
            zIndex: 1,
          },
          '.crp-cell--overflow-visible': {
            overflow: 'visible',
          },
          '.MuiDataGrid-row:has(.directive-cell--overflow-visible)': {
            overflow: 'visible',
            position: 'relative',
          },
          '.MuiDataGrid-row:has(.directive-cell--overflow-visible):hover': {
            zIndex: 1,
          },
          '.directive-cell--overflow-visible': {
            overflow: 'visible',
          },
          '.Mui-DatagridRow-even': {
            backgroundColor: skapaColours.colourNeutral2,
          },
          '.MuiDataGrid-columnHeaderTitle': {
            color: skapaColours.colourNeutral6,
          },
        }}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0
            ? 'Mui-DatagridRow-even'
            : 'Mui-DatagridRow-odd'
        }
        slots={{ toolbar: TableToolBar }}
        columnVisibilityModel={{
          ...tableSettings.datagridProps.columnVisibilityModel,
        }}
        checkboxSelection
        disableRowSelectionOnClick
        pageSizeOptions={[10, 25, 50, 100]}
        slotProps={{
          panel: {
            anchorEl: filterAnchorEl,
            placement: 'bottom-end',
          },
          toolbar: {
            setFilterAnchorEl: setFilterAnchorEl,
            children: tableSelection.length ? (
              <>
                {isArchiveButtonVisible && (
                  <Button
                    type="secondary"
                    size="small"
                    onClick={archiveHandler}
                  >
                    Archive Projects
                  </Button>
                )}
                <Button
                  size="small"
                  onClick={() => {
                    setSelectedFY('');
                    setIsCaptureRevisedBudgetModal(true);
                  }}
                >
                  {t('ppm.actions.captureRevisedBudget')}
                </Button>
                <Button type="primary" size="small" onClick={approveProject}>
                  {t('ppm.actions.approve')}
                </Button>
              </>
            ) : undefined,

            saveSettings: tableSettings.saveSettings,
            resetSettings: tableSettings.resetSettings,
            filters: [
              {
                label: 'Countries',
                options: countryOptions,
                selectedValues: selectedCountries,
                onToggle: (value) => {
                  toggleFilter({
                    field: 'countryCode',
                    value,
                    operator: 'isAnyOf',
                  });
                  onSelectedCountriesChange(value);
                },
                id: getIdFromLabel('Countries'),
                defaultToggle: true,
                addSearch: true,
                limitedHeight: true,
                footer: (
                  <ListBoxFooter>
                    <Button
                      fluid
                      size={'small'}
                      type="secondary"
                      text="Select None"
                      onClick={() => toggleCountries(false)}
                    />
                    <Button
                      fluid
                      size={'small'}
                      type="secondary"
                      text="Select All"
                      onClick={() => toggleCountries(true)}
                    />
                  </ListBoxFooter>
                ),
              },
              {
                component: <FYBudgetFilter />,
              },
              {
                label: 'ISEP',
                options: [
                  { label: 'Yes', value: true },
                  { label: 'No', value: false },
                ],
                selectedValues: searchAppliedFilterValues('isep'),
                defaultToggle: true,
                id: getIdFromLabel('isep'),
                onToggle: (value) =>
                  toggleFilter({ field: 'isep', value, operator: 'isAnyOf' }),
              },
              {
                label: t('ppm.columns.subPortfolio'),
                options: Object.keys(subPortfolioOptions || {}).map((key) => ({
                  value: subPortfolioOptions[key],
                  label: subPortfolioOptions[key],
                })),
                selectedValues: searchAppliedFilterValues('subPortfolio'),
                onToggle: (value) =>
                  toggleFilter({
                    field: 'subPortfolio',
                    value,
                    operator: 'isAnyOf',
                  }),
                id: getIdFromLabel('subPortfolio'),
                defaultToggle: true,
                limitedHeight: true,
              },
              {
                label: 'Views',
                options: [
                  { label: 'Default view', value: 'default' },
                  { label: 'Financial view', value: 'financial' },
                  { label: 'Next FY Forecast', value: 'forecast' },
                  { label: 'Lada', value: 'lada' },
                ],

                selectedValues: selectedView,
                onToggle: (value) => {
                  setSelectedView([value]);
                  viewClicked(value, tableSettings, t);
                },
                id: getIdFromLabel('view'),
              },
            ],
          },
        }}
        pagination
        groupingColDef={{
          renderCell: groupColumn,
        }}
        onRowSelectionModelChange={setTableSelection}
        rowSelectionModel={tableSelection}
        rowGroupingColumnMode={'multiple'}
        columns={setupColumnsWidthAndOrder(
          columns,
          tableSettings.datagridProps.columnSizeModel,
          tableSettings.datagridProps.columnOrderModel,
        )}
        rows={projects}
      />
      <CaptureRevisedBudgetPrompt
        isVisible={isCaptureRevisedBudgetModal}
        onClose={() => setIsCaptureRevisedBudgetModal(false)}
        onCancel={() => setIsCaptureRevisedBudgetModal(false)}
        onAccept={() => {
          captureRevisedBudget({
            projectIds: tableSelection,
            fiscalYear: selectedFY,
          });
          setIsCaptureRevisedBudgetModal(false);
        }}
        selectedFY={selectedFY}
        onFYChange={setSelectedFY}
        fyOptions={fyOptions}
      />
    </Stack>
  );
};

export default ApprovalTable;
